import { useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { Button } from 'flowbite-react';
import { CheckIcon, XIcon } from 'lucide-react';
import { useCallback } from 'react';
import { useNavigate, useParams } from 'react-router';

import { useApi } from '../../../api/useApi.hook';
import { ErrorSwitch } from '../../../components/errorMessage/errorSwitch.component';
import { pathWithParams } from '../../../lib/routing.utils';
import { AppHeader } from '../../components/appHeader/appHeader.component';
import { RouteIndex } from '../../routeIndex';

const DocumentPaymentReturnView = () => {
    const api = useApi();
    const queryClient = useQueryClient();
    const nav = useNavigate();

    const { documentId } = useParams<{ documentId: string }>();

    const document = useQuery({
        queryKey: ['document', documentId],
        queryFn: async () => {
            return api.v1.documents.getById(documentId ?? '');
        }
    });

    const gopayPaymentState = useQuery({
        queryKey: ['documentGoPayPaymentState', documentId],
        queryFn: async () => {
            return api.v1.documents.verifyGoPayPaymentState(documentId ?? '');
        }
    });

    const gpWebpayPaymentState = useQuery({
        queryKey: ['documentGpWebpayPaymentState', documentId],
        queryFn: async () => {
            return api.v1.documents.getGpWebpayPaymentState(documentId ?? '');
        }
    });

    const handleContinueClick = useCallback(async () => {
        await queryClient.invalidateQueries({ queryKey: ['document', documentId] });

        nav(pathWithParams(
            RouteIndex.documentDetail.edit,
            { documentId: documentId ?? '' }
        ));
    }, []);

    let state: 'paid'|'cancelled'|'refunded'|'other'|'notFound'|undefined = undefined;

    // Check GoPay state
    if (gopayPaymentState.data?.state === 'PAID') {
        state = 'paid';
    } else if (gopayPaymentState.data?.state === 'CANCELLED') {
        state = 'cancelled';
    } else if (gopayPaymentState.data?.state === 'REFUNDED' || gopayPaymentState.data?.state === 'PARTIALLY_REFUNDED') {
        state = 'refunded';
    } else if (!!gopayPaymentState.data?.state) {
        state = 'other';
    } else if (gopayPaymentState.data?.state === null) {
        state = 'notFound';
    }

    // Override with GP WebPay state if applicable
    if (gpWebpayPaymentState.data?.status === 'CAPTURED') {
        state = 'paid';
    } else if (gpWebpayPaymentState.data?.status === 'UNPAID' && state === 'notFound') {
        state = 'other';
    } else if (gpWebpayPaymentState.data?.status === 'REFUNDED' || gpWebpayPaymentState.data?.status === 'REVERSED') {
        state = 'refunded';
    }

    // If the document itself is marked as paid, that's the final truth
    if (document.data?.paidForAt) {
        state = 'paid';
    }

    return (
        <>
            <AppHeader
                pageTitle={document.data?.documentName ?? '...'}
                backLink={RouteIndex.documentList}
            >
            </AppHeader>

            <div className="flex flex-col justify-start items-center min-h-screen pt-14">
                <div className='flex flex-col items-center justify-center gap-6 p-4 md:p-8 lg:py-12 w-[36rem] max-w-full min-h-[calc(100vh-5rem)]'>

                    <ErrorSwitch
                        error={document.error}
                        default={{ message: 'Při načítání dat došlo k chybě. Zkuste to prosím za chvíli znovu.', showDetails: true }}
                        options={[]}
                        retry={() => queryClient.invalidateQueries({ queryKey: ['document', documentId] })}
                    />

                    {!!document.data && (
                        <>
                            {state === undefined && (
                                <div className={clsx(
                                    'relative justify-center w-[8rem] aspect-square rounded-full text-2xl font-bold border border-gray-200'
                                )}>
                                    <div className='absolute left-1 top-1 right-1 bottom-1 rounded-full [background:conic-gradient(#3498db_0deg_270deg,transparent_270deg_360deg)] [animation:spin_3s_linear_infinite]' />
                                    <div className='absolute left-2 top-2 right-2 bottom-2 rounded-full flex flex-row items-center justify-center bg-gray-100 text-gray-700'></div>
                                </div>
                            )}

                            {state === 'paid' && (
                                <div className='flex flex-col items-center gap-6'>
                                    <div className={clsx(
                                        'relative justify-center w-[8rem] aspect-square rounded-full text-2xl font-bold border-[3px] border-lime-600',
                                        'flex flex-row items-center justify-center text-lime-600 pt-2'
                                    )}>
                                        <CheckIcon size={64} />
                                    </div>
                                    <p className='text-xl font-semibold text-lime-700'>Vaše platba byla přijata.</p>
                                    <Button
                                        color="primary"
                                        size='lg'
                                        onClick={handleContinueClick}
                                    >Pokračovat</Button>
                                </div>
                            )}

                            {state === 'cancelled' && (
                                <div className='flex flex-col items-center gap-6'>
                                    <div className={clsx(
                                        'relative justify-center w-[8rem] aspect-square rounded-full text-2xl font-bold border-[3px] border-gray-600',
                                        'flex flex-row items-center justify-center text-gray-600 pt-2'
                                    )}>
                                        <XIcon size={64} />
                                    </div>
                                    <p className='text-xl font-semibold text-gray-700'>Vaše platba byla zrušena.</p>
                                    <Button
                                        color="primary"
                                        size='lg'
                                        onClick={handleContinueClick}
                                    >Pokračovat zpět na dokument</Button>
                                </div>
                            )}

                            {state === 'refunded' && (
                                <div className='flex flex-col items-center gap-6'>
                                    <div className={clsx(
                                        'relative justify-center w-[8rem] aspect-square rounded-full text-2xl font-bold border-[3px] border-lime-600',
                                        'flex flex-row items-center justify-center text-lime-600 pt-2'
                                    )}>
                                        <CheckIcon size={64} />
                                    </div>
                                    <p className='text-xl font-semibold text-lime-700'>Vaše platba byla vrácena.</p>
                                    <Button
                                        color="primary"
                                        size='lg'
                                        onClick={handleContinueClick}
                                    >Pokračovat</Button>
                                </div>
                            )}

                            {state === 'other' && (
                                <div className='flex flex-col items-center gap-6'>
                                    <div className={clsx(
                                        'relative justify-center w-[8rem] aspect-square rounded-full text-2xl font-bold border-[3px] border-gray-600',
                                        'flex flex-row items-center justify-center text-gray-600 pt-2'
                                    )}>
                                        <XIcon size={64} />
                                    </div>
                                    <p className='text-xl font-semibold text-gray-700'>Vaše platba nebyla dokončena.</p>
                                    <Button
                                        color="primary"
                                        size='lg'
                                        onClick={handleContinueClick}
                                    >Pokračovat zpět na dokument</Button>
                                </div>
                            )}

                            {state === 'notFound' && (
                                <div className='flex flex-col items-center gap-6'>
                                    <div className={clsx(
                                        'relative justify-center w-[8rem] aspect-square rounded-full text-2xl font-bold border-[3px] border-gray-600',
                                        'flex flex-row items-center justify-center text-gray-600 pt-2'
                                    )}>
                                        <XIcon size={64} />
                                    </div>
                                    <p className='text-xl font-semibold text-gray-700'>ID platby nebylo nalezeno.</p>
                                    <Button
                                        color="primary"
                                        size='lg'
                                        onClick={handleContinueClick}
                                    >Pokračovat zpět na dokument</Button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </>
    );
};

export default DocumentPaymentReturnView;
